import { ToolItem } from '../types';

export interface FileToolItem extends ToolItem {
  fileCategory: 'archives' | 'inspection' | 'data-viewers' | 'file-utils';
  fileCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const FILE_CATEGORIES_TABS = [
  { id: 'all', label: 'All File Tools', icon: 'FolderArchive' },
  { id: 'archives', label: 'Archives & Compression', icon: 'Archive' },
  { id: 'inspection', label: 'Checksums & Inspection', icon: 'FileSearch' },
  { id: 'data-viewers', label: 'Data & File Viewers', icon: 'FileCode' },
  { id: 'file-utils', label: 'File Utilities & Batch', icon: 'Files' }
];

export const FILE_TOOLS_DATA: FileToolItem[] = [
  // --- ARCHIVES & COMPRESSION ---
  {
    id: 'zip-viewer',
    name: 'ZIP Viewer',
    description: 'Inspect archive directory structures, file lists, and preview contents inside ZIP files without extraction.',
    fileCategory: 'archives',
    fileCategoryLabel: 'Archives & Compression',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Archive',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'zip-creator',
    name: 'ZIP Creator',
    description: 'Compress multiple files and folders into encrypted or standard ZIP archive files right in browser.',
    fileCategory: 'archives',
    fileCategoryLabel: 'Archives & Compression',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'FolderArchive',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'image-to-zip',
    name: 'Image to ZIP',
    description: 'Bundle multiple image uploads into a clean, structured ZIP file download package.',
    fileCategory: 'archives',
    fileCategoryLabel: 'Archives & Compression',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'FileImage',
    actionType: 'generic'
  },
  {
    id: 'file-splitter',
    name: 'File Splitter',
    description: 'Split large files into smaller chunk files by byte size or chunk quantity for easy transfers.',
    fileCategory: 'archives',
    fileCategoryLabel: 'Archives & Compression',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Scissors',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'file-joiner',
    name: 'File Joiner',
    description: 'Reassemble previously split binary or text chunk files back into their original single file.',
    fileCategory: 'archives',
    fileCategoryLabel: 'Archives & Compression',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Combine',
    actionType: 'generic'
  },

  // --- CHECKSUMS & INSPECTION ---
  {
    id: 'file-checksum',
    name: 'File Checksum',
    description: 'Generate MD5, SHA-1, SHA-256, and SHA-512 cryptographic hashes for local files instantly.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'ShieldCheck',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'checksum-compare',
    name: 'Checksum Compare',
    description: 'Compare file hash against an expected checksum string to verify file integrity and authenticity.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'FileCheck',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'file-hash',
    name: 'File Hash Generator',
    description: 'Calculate multi-algorithm file signatures (CRC32, SHA3, BLAKE2) for download verification.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'file-metadata-viewer',
    name: 'File Metadata Viewer',
    description: 'Inspect embedded file metadata headers, creation timestamps, author attributes, and EXIF tags.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Info',
    actionType: 'generic'
  },
  {
    id: 'mime-viewer',
    name: 'MIME Type Viewer',
    description: 'Detect true MIME content types and magic numbers regardless of misleading file extensions.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Search',
    actionType: 'generic'
  },
  {
    id: 'file-extension-checker',
    name: 'File Extension Checker',
    description: 'Analyze file headers and match binary signatures against known file extension formats.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'FileQuestion',
    actionType: 'generic'
  },
  {
    id: 'hex-viewer',
    name: 'Hex Viewer',
    description: 'Examine raw byte sequences and hexadecimal memory representations of any uploaded file.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Binary',
    actionType: 'generic'
  },
  {
    id: 'binary-viewer',
    name: 'Binary Viewer',
    description: 'Inspect binary bitstreams (0s and 1s) and raw machine code layouts of files.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-[#6366f1]/15',
    iconTextColor: 'text-[#6366f1]',
    iconName: 'Cpu',
    actionType: 'generic'
  },
  {
    id: 'file-compare',
    name: 'File Compare (Diff)',
    description: 'Side-by-side text and line difference viewer to compare two files for modifications.',
    fileCategory: 'inspection',
    fileCategoryLabel: 'Checksums & Inspection',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'GitCompare',
    isPopular: true,
    actionType: 'generic'
  },

  // --- DATA & FILE VIEWERS ---
  {
    id: 'csv-viewer',
    name: 'CSV Viewer',
    description: 'Open, filter, search, and sort tabular data inside CSV files with rich grid display.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Table',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'csv-editor',
    name: 'CSV Editor',
    description: 'Edit rows, columns, headers, and values inside CSV files and export back to clean CSV/TSV.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Edit3',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'excel-viewer',
    name: 'Excel Viewer',
    description: 'Preview XLSX and XLS spreadsheet workbooks and worksheets directly in the browser.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'FileSpreadsheet',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'csv-merge',
    name: 'CSV Merge',
    description: 'Combine multiple CSV spreadsheet files into a unified master dataset table.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Merge',
    actionType: 'generic'
  },
  {
    id: 'csv-split',
    name: 'CSV Split',
    description: 'Split large CSV files into multiple smaller CSV files by row count limits.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Split',
    actionType: 'generic'
  },
  {
    id: 'json-viewer',
    name: 'JSON Viewer',
    description: 'Format, validate, collapse, and tree-view complex JSON structured files.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'FileCode',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'xml-viewer',
    name: 'XML Viewer',
    description: 'Format and inspect hierarchical XML markup trees with element syntax highlighting.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'yaml-viewer',
    name: 'YAML Viewer',
    description: 'Parse, validate, and convert YAML configuration files to interactive tree objects.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'markdown-viewer',
    name: 'Markdown Viewer',
    description: 'Render formatted HTML previews of Markdown (.md) documents with syntax highlighting.',
    fileCategory: 'data-viewers',
    fileCategoryLabel: 'Data & File Viewers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Eye',
    actionType: 'generic'
  },

  // --- FILE UTILITIES & BATCH ---
  {
    id: 'file-size-calculator',
    name: 'File Size Calculator',
    description: 'Convert file sizes between Bytes, KB, MB, GB, TB, PB and calculate storage transfer times.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'HardDrive',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'folder-size-calculator',
    name: 'Folder Size Calculator',
    description: 'Calculate cumulative directory storage usage, file count breakdowns, and largest subfolders.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Folder',
    actionType: 'generic'
  },
  {
    id: 'rename-files',
    name: 'Rename Files',
    description: 'Batch rename multiple files using custom prefixes, suffixes, numbering, and patterns.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'PenTool',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'batch-rename-preview',
    name: 'Batch Rename Preview',
    description: 'Preview regex search-and-replace rules on file lists before applying modifications.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'FileSearch',
    actionType: 'generic'
  },
  {
    id: 'duplicate-file-finder',
    name: 'Duplicate File Finder',
    description: 'Scan selected files for identical byte contents or matching checksums to clear duplicates.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Copy',
    actionType: 'generic'
  },
  {
    id: 'text-to-file',
    name: 'Text to File',
    description: 'Convert raw copied text snippets into downloadable .txt, .json, .csv, or custom files.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'FilePlus',
    actionType: 'generic'
  },
  {
    id: 'text-file-cleaner',
    name: 'Text File Cleaner',
    description: 'Strip empty lines, duplicate rows, whitespace trailing spaces, and non-printable characters from text files.',
    fileCategory: 'file-utils',
    fileCategoryLabel: 'File Utilities & Batch',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  }
];
